import { MobileTicketIcon, TicketSendIcon, TicketsGetIcon, TicketsIcon, TicketsSecureIcon, TicketsSendIcon } from '@tix-ticketing/ui-icons/feature';
import { FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import type { DeliveryTypeDto, DonationDto, EventTicketGroupDto, OrderDonationDto, OrderMembershipDto, OrderResponse, OrderServiceDto, PaymentDto, ServiceType, SubscriptionTypeGroupDto, TicketDto } from 'api/generated';
import { isExpired } from './date-helpers';

/**
 * Checks if an API response indicates the order was not found (404).
 * If so, clears the locally cached order.
 * Returns `true` if the order was not found, so callers can short-circuit.
 * Callers are responsible for showing error feedback (e.g. via `toastError`).
 */
export const handleOrderNotFound = (response: {
  status?: number;
}, clearOrder: () => void): boolean => {
  if (response?.status === 404) {
    clearOrder();
    return true;
  }
  return false;
};

/**
 * Checks if an API response indicates tickets were not found (404).
 * This typically means they expired server-side before the client timer fired.
 * Clears the locally cached reservation.
 * Returns `true` if tickets were not found, so callers can short-circuit.
 * Callers are responsible for showing error feedback (e.g. via `toastError`).
 */
export const handleTicketsExpired = (response: {
  status?: number;
}, clearReservation: () => void): boolean => {
  if (response?.status === 404) {
    clearReservation();
    return true;
  }
  return false;
};

/**
 * Donations the server has resolved for this order (basket-rule or
 * offered-with-purchase). These come pre-attached to the order, so the donation
 * step never needs to fetch them.
 */
export const getAvailableDonations = (order: OrderResponse | undefined): DonationDto[] => order?.availableDonations ?? [];

/**
 * Returns every event ticket group in the order. Tickets bought directly live
 * in `order.tickets`, while subscription tickets are nested under
 * `order.subscriptionTypeGroups[].eventTicketGroups`. Both need to be included
 * so subscription tickets show up against their events in the cart.
 */
export const getAllEventTicketGroups = (order: OrderResponse | undefined): EventTicketGroupDto[] => {
  if (!order) return [];
  const directGroups = order.tickets ?? [];
  const subscriptionGroups = (order.subscriptionTypeGroups ?? []).flatMap(group => group.eventTicketGroups ?? []);
  return [...directGroups, ...subscriptionGroups];
};

/**
 * Subscription orders carry one event ticket group per purchased seat-set, so
 * the same event can appear in several groups. Collect tickets from all of
 * them, not just the first match.
 */
export const getEventTickets = (order: OrderResponse | undefined, eventId: number | undefined): TicketDto[] => {
  if (!order || eventId == null) return [];
  return getAllEventTicketGroups(order).filter(g => g.eventId === eventId).flatMap(g => g.tickets ?? []);
};

/** Whether the order's tickets to an event are eligible for a refund — the
 *  server-computed flag on that event's ticket group, covering both plain and
 *  subscription tickets the same way `getExchangeableTicketGroup` does for
 *  exchanges. */
export const canRefundEventTickets = (order: OrderResponse | undefined, eventId: number | undefined): boolean => {
  if (!order || eventId == null) return false;
  return getAllEventTicketGroups(order).some(g => g.eventId === eventId && g.canRefundTickets);
};
export const getAllOrderTickets = (order: OrderResponse | undefined): TicketDto[] => {
  return getAllEventTicketGroups(order).flatMap(g => g.tickets ?? []);
};

/**
 * Applies a transform to every ticket array in the order, covering both the
 * direct ticket groups (`order.tickets`) and the nested subscription ticket
 * groups (`order.subscriptionTypeGroups[].eventTicketGroups`). Used so cart
 * mutations (remove ticket, change ticket type) stay consistent regardless of
 * whether the ticket was bought directly or as part of a subscription.
 */
export const mapOrderTicketGroups = (order: OrderResponse, mapTickets: (tickets: TicketDto[]) => TicketDto[]): OrderResponse => ({
  ...order,
  tickets: (order.tickets ?? []).map(group => ({
    ...group,
    tickets: mapTickets(group.tickets ?? [])
  })),
  subscriptionTypeGroups: (order.subscriptionTypeGroups ?? []).map(subGroup => ({
    ...subGroup,
    eventTicketGroups: (subGroup.eventTicketGroups ?? []).map(group => ({
      ...group,
      tickets: mapTickets(group.tickets ?? [])
    }))
  }))
});

/**
 * The API returns one `SubscriptionTypeGroupDto` per purchased subscription
 * instance (i.e. one per seat-set), so buying N seats yields N groups that all
 * share the same `subscriptionTypeId`, each holding one ticket per event. The
 * cart shows a single card per subscription type with every ticket grouped
 * under its event, so merge groups by `subscriptionTypeId`: combine their
 * `eventTicketGroups` by `eventId`, sum the price, and recompute the event and
 * ticket counts from the merged data.
 *
 * The merged events follow `order.events`, so an event keeps its place however
 * the seat-sets it appears in are arranged — a seat-set that is missing one
 * event, or has lost one to a removal, would otherwise push it to the end.
 */
export const getMergedSubscriptionGroups = (order: OrderResponse | undefined): SubscriptionTypeGroupDto[] => {
  const groups = order?.subscriptionTypeGroups ?? [];
  const mergedById = new Map<number, SubscriptionTypeGroupDto>();
  const eventOrder = new Map((order?.events ?? []).map((event, index) => [event.id, index]));
  const eventPosition = (eventId: number | null | undefined) => eventOrder.get(eventId ?? undefined) ?? Number.MAX_SAFE_INTEGER;
  for (const group of groups) {
    const key = group.subscriptionTypeId ?? -1;
    const existing = mergedById.get(key);
    if (!existing) {
      mergedById.set(key, {
        ...group,
        eventTicketGroups: (group.eventTicketGroups ?? []).map(etg => ({
          ...etg,
          tickets: [...(etg.tickets ?? [])]
        }))
      });
      continue;
    }
    const eventTicketGroups = existing.eventTicketGroups ?? [];
    for (const etg of group.eventTicketGroups ?? []) {
      const target = eventTicketGroups.find(e => e.eventId === etg.eventId);
      if (target) {
        target.tickets = [...(target.tickets ?? []), ...(etg.tickets ?? [])];
      } else {
        eventTicketGroups.push({
          ...etg,
          tickets: [...(etg.tickets ?? [])]
        });
      }
    }
    existing.eventTicketGroups = eventTicketGroups;
    existing.totalPrice = (existing.totalPrice ?? 0) + (group.totalPrice ?? 0);
  }
  return Array.from(mergedById.values()).map(group => {
    const eventTicketGroups = [...(group.eventTicketGroups ?? [])].sort((a, b) => eventPosition(a.eventId) - eventPosition(b.eventId));
    return {
      ...group,
      eventTicketGroups,
      eventCount: eventTicketGroups.length,
      ticketCount: eventTicketGroups.reduce((acc, etg) => acc + (etg.tickets?.length ?? 0), 0)
    };
  });
};

/**
 * The single subscription an order consists of, if it holds nothing else:
 * no directly bought tickets, gift cards, memberships or donations, and only
 * one subscription type. Order history shows those orders under the
 * subscription's own name and image rather than the first event's.
 */
export const getSoleSubscriptionGroup = (order: OrderResponse | undefined): SubscriptionTypeGroupDto | undefined => {
  if (!order) return undefined;
  const hasOtherItems = (order.tickets ?? []).some(group => (group.tickets?.length ?? 0) > 0) || (order.giftCards?.length ?? 0) > 0 || (order.memberships?.length ?? 0) > 0 || (order.donations?.length ?? 0) > 0;
  if (hasOtherItems) return undefined;
  const merged = getMergedSubscriptionGroups(order);
  return merged.length === 1 ? merged[0] : undefined;
};
export const getOrderTicketsCount = (order: OrderResponse | undefined): number => {
  return getAllOrderTickets(order).length;
};
export const getOrderTicketsTotal = (order: OrderResponse | undefined): number => {
  return getAllOrderTickets(order).reduce((acc, ticket) => {
    return acc + (ticket.priceWithTax ?? 0);
  }, 0);
};
export const getEventTicketsTotal = (order: OrderResponse | undefined, eventId: number | undefined): number => {
  const tickets = getEventTickets(order, eventId);
  return tickets.reduce((acc: number, ticket: TicketDto) => {
    return acc + (ticket.priceWithFeesAndTax ?? 0);
  }, 0);
};

// Pre-discount counterpart to getEventTicketsTotal, for showing a struck-through
// original total beside the discounted one. Falls back to the current price per
// ticket when it carries no original price of its own (i.e. wasn't discounted).
export const getEventTicketsOriginalTotal = (order: OrderResponse | undefined, eventId: number | undefined): number => {
  const tickets = getEventTickets(order, eventId);
  return tickets.reduce((acc: number, ticket: TicketDto) => {
    return acc + (ticket.originalPriceWithFeesAndTax ?? ticket.priceWithFeesAndTax ?? 0);
  }, 0);
};
export const getTicketsTotal = (tickets: TicketDto[] | undefined): number => {
  if (!tickets) return 0;
  return tickets.reduce((acc: number, ticket: TicketDto) => {
    return acc + (ticket.priceWithFeesAndTax ?? 0);
  }, 0);
};
export const orderEventTotalTicketPrices = (order: OrderResponse | undefined, eventId: number | undefined): number => {
  if (!order || !eventId) return 0;
  const tickets = getEventTickets(order, eventId);
  const total = tickets.reduce((acc: number, ticket: TicketDto) => {
    return acc + (ticket.priceWithFeesAndTax ?? 0);
  }, 0);
  return total;
};
export const getOrderMembershipsCount = (order: OrderResponse | undefined): number => {
  return order?.memberships?.length ?? 0;
};
export const getOrderMembershipsTotal = (order: OrderResponse | undefined): number => {
  if (!order) return 0;
  return (order.memberships ?? []).reduce((acc: number, membership: OrderMembershipDto) => {
    return acc + (membership.price ?? 0);
  }, 0);
};
export const getOrderDonationsCount = (order: OrderResponse | undefined): number => {
  return order?.donations?.length ?? 0;
};
export const getOrderDonationsTotal = (order: OrderResponse | undefined): number => {
  if (!order) return 0;
  return (order.donations ?? []).reduce((acc: number, donation: OrderDonationDto) => {
    return acc + (donation.price ?? 0);
  }, 0);
};
export const getOrderGiftCardsCount = (order: OrderResponse | undefined): number => {
  return order?.giftCards?.length ?? 0;
};
export const getOrderServicesCount = (order: OrderResponse | undefined): number => {
  return order?.services?.length ?? 0;
};

/** Add-on products (food & drink and the like) across all events. */
export const getOrderProductsCount = (order: OrderResponse | undefined): number => {
  return order?.products?.length ?? 0;
};

/** What the order's add-on products come to. Each unit is its own row. */
export const getOrderProductsTotal = (order: OrderResponse | undefined): number => (order?.products ?? []).reduce((acc, product) => acc + (product.priceWithTax ?? 0), 0);

/**
 * The order's service rows that are shown as charges below the subtotal. Which
 * ones those are is the backend's call, since it is the same split that decides
 * what `order.subtotal` counts, and the lines only add up to the total while the
 * two agree. `types` narrows it further, for a caller that prices the rest
 * another way.
 */
export const getOrderChargeServices = (order: OrderResponse | undefined, types?: ServiceType[]): OrderServiceDto[] => (order?.services ?? []).filter(service => service.isCharge && (!types || !!service.type && types.includes(service.type)));

/**
 * Those charges as one row per kind: an order exchanged twice carries a change
 * fee per swap, and the buyer reads them as one charge taken twice rather than
 * as the same line printed again. `price` is what the kind comes to in total.
 */
export const getOrderChargeGroups = (order: OrderResponse | undefined, types?: ServiceType[]): {
  service: OrderServiceDto;
  count: number;
  price: number;
}[] => getOrderChargeServices(order, types).reduce<{
  service: OrderServiceDto;
  count: number;
  price: number;
}[]>((groups, service) => {
  const group = groups.find(held => chargeKind(held.service) === chargeKind(service));
  if (group) {
    group.count += 1;
    group.price += service.price ?? 0;
    return groups;
  }
  return [...groups, {
    service,
    count: 1,
    price: service.price ?? 0
  }];
}, []);

/** What tells two charge rows apart, which is what their label reads off. */
const chargeKind = (service: OrderServiceDto) => service.type ?? `name:${service.name ?? ''}`;

/**
 * Label for a service charge, counted when the order carries the charge more
 * than once. The name is a per-tenant reference value and is empty on a tenant
 * that never seeded the row, so known types are translated and the name is only
 * a fallback.
 */
export const getOrderServiceLabel = (service: OrderServiceDto, count = 1) => {
  const label = serviceTypeLabel(service);
  return count > 1 ? <FormattedMessage id="common.xTimesCharge" defaultMessage={[{
    "type": 1,
    "value": "count"
  }, {
    "type": 0,
    "value": " x "
  }, {
    "type": 1,
    "value": "label"
  }]} values={{
    count,
    label
  }} /> : label;
};
const serviceTypeLabel = (service: OrderServiceDto) => {
  switch (service.type) {
    case 'Delivery':
      return <FormattedMessage id="common.deliveryFee" defaultMessage={[{
        "type": 0,
        "value": "Delivery fee"
      }]} />;
    case 'TransactionFee':
      return <FormattedMessage id="common.orderFee" defaultMessage={[{
        "type": 0,
        "value": "Order fee"
      }]} />;
    case 'ChangeEvent':
      return <FormattedMessage id="exchangeCart.changeFee" defaultMessage={[{
        "type": 0,
        "value": "Ticket exchange fee"
      }]} />;
    default:
      return <>{service.name ?? ''}</>;
  }
};

/**
 * How many items the cart is holding, for the navbar badge and the cart's own
 * count. `order.services` are fee lines the backend attaches on its own —
 * delivery, card and transaction fees, order protection — so they are left out:
 * they are not something the buyer put in the cart.
 */
export const getOrderItemCount = (order: OrderResponse | undefined): number => {
  return getOrderTicketsCount(order) + getOrderGiftCardsCount(order) + getOrderMembershipsCount(order) + getOrderDonationsCount(order) + getOrderProductsCount(order);
};

/**
 * What an order is made of, used to pick wording and actions that talk about
 * the actual purchase ("your membership is ready") instead of always assuming
 * tickets. `mixed` covers anything spanning more than one item type, plus
 * services on their own — they have no wording of their own and read as a
 * generic order.
 */
export type OrderContentKind = 'tickets' | 'membership' | 'giftCard' | 'donation' | 'mixed';
export const getOrderContentKind = (order: OrderResponse | undefined): OrderContentKind => {
  const counts = {
    tickets: getOrderTicketsCount(order),
    membership: getOrderMembershipsCount(order),
    giftCard: getOrderGiftCardsCount(order),
    donation: getOrderDonationsCount(order),
    service: getOrderServicesCount(order),
    product: getOrderProductsCount(order)
  };
  const present = Object.entries(counts).filter(([, count]) => count > 0);
  if (present.length !== 1) return 'mixed';

  // Services or products on their own get the generic wording — they read as a
  // plain order.
  const [only] = present[0]!;
  return only === 'service' || only === 'product' ? 'mixed' : only as OrderContentKind;
};

/**
 * Whether the order's hold has run out. A Box-Office reservation can be set to
 * never expire, which is not the same as being expired.
 */
export const isOrderExpired = (order: OrderResponse | undefined): boolean => order?.expires ? isExpired(order.expires) : false;

/**
 * An order drives the reservation countdown only while it is a live, unpaid
 * cart. Once it is sold/paid it is no longer an active reservation and should
 * be cleared from app state so it does not keep a stale timer running across
 * the app (e.g. while browsing My Pages).
 */
export const isActiveReservation = (order: OrderResponse | undefined): boolean => {
  if (!order) return false;
  return !order.isSold && !order.paidAt;
};

/**
 * Count of every non-ticket item in the order — products, donations,
 * memberships and gift cards. Shown as the "Other" stat alongside the ticket
 * count in the sticky order toolbar. Fee lines in `order.services` are left
 * out, as in `getOrderItemCount`.
 */
export const getOrderOtherItemsCount = (order: OrderResponse | undefined): number => {
  return getOrderGiftCardsCount(order) + getOrderMembershipsCount(order) + getOrderDonationsCount(order) + getOrderProductsCount(order);
};
export const getOrderGiftCardsTotal = (order: OrderResponse | undefined): number => {
  if (!order) return 0;
  return (order.giftCards ?? []).reduce((acc, giftCard) => {
    return acc + (giftCard.price ?? 0);
  }, 0);
};

/**
 * "Paid with X" summary label: the settled non-gift-card payment type, or the
 * (caller-localized) gift-card label only when gift cards fully cover the order.
 */
export const getOrderPaymentLabel = (order: OrderResponse | undefined, giftCardLabel: string): string | null => {
  const payments = order?.payments ?? [];
  const settled = payments.find((p: PaymentDto) => !p.isGiftCard);
  if (settled) return settled.paymentTypeName ?? null;
  if (payments.some((p: PaymentDto) => p.isGiftCard)) return giftCardLabel;
  return null;
};
export const orderTotalTicketSeparateFees = (order: OrderResponse | undefined): number => {
  let totalFees = 0;
  for (const ticket of getAllOrderTickets(order)) {
    ticket?.fees?.filter(fee => fee.showAsSeparateItem).forEach(fee => {
      totalFees += fee.price ?? 0;
    });
  }
  return totalFees;
};
export const getDeliveryTypeIcon = (deliveryType: DeliveryTypeDto | undefined) => {
  if (deliveryType?.isMobile) return <MobileTicketIcon />;
  if (deliveryType?.isPdf) return <TicketsGetIcon />;
  if (deliveryType?.isSecureTicket) return <TicketsSecureIcon />;
  if (deliveryType?.isSecureWallet) return <TicketsSecureIcon />;
  if (deliveryType?.isPickUp) return <TicketSendIcon />;
  if (deliveryType?.isDelivery) return <TicketsSendIcon />;
  return <TicketsIcon />;
};
export const ordersContainingEventId = (orders: OrderResponse[], eventId: number) => {
  return orders.filter(order => order.events?.some(event => event.id === eventId));
};
export const getEventTicketsCount = (eventId: number, orders: OrderResponse[]) => {
  return ordersContainingEventId(orders, eventId).reduce((acc, order) => {
    acc += getEventTickets(order, eventId).length;
    return acc;
  }, 0);
};

/**
 * A renewal offer is an unpaid order the Box Office created from a renewing
 * subscription.
 */
export const isRenewalOffer = (order: OrderResponse): boolean => !order.isSold && !!order.isRenewal;

/**
 * An unpaid order the buyer should see on My pages at all. The venue can hide a
 * Box-Office reservation from My pages outright or until a date, and an expired
 * reservation is gone whether or not the venue deletes it automatically.
 */
const isVisibleOffer = (order: OrderResponse): boolean => !order.isSold && !order.hideOnMyPages && !isOrderExpired(order);

/**
 * An unpaid order the buyer should see on the Reservations tab. Only an order
 * the API marks as a reservation counts — one the Box Office made, or a basket
 * the buyer reserved themselves — never the buyer's ordinary signed-in cart. A
 * response without the flag is taken as a reservation. Renewal offers are
 * reservations too, so they have to be excluded explicitly — they have a tab
 * of their own, with actions of their own.
 */
export const isVisibleReservation = (order: OrderResponse): boolean => isVisibleOffer(order) && order.isReservation !== false && !isRenewalOffer(order);

/** A renewal offer the buyer should see on the Renewals tab. */
export const isVisibleRenewal = (order: OrderResponse): boolean => isVisibleOffer(order) && isRenewalOffer(order);

/** Expiry as a sortable instant, with "never expires" pushed to the end. */
const expiryRank = (order: OrderResponse): number => order.expires ? new Date(order.expires).getTime() : Number.MAX_SAFE_INTEGER;

/**
 * Search + sort for the My pages order lists (Reservations, Renewals). Search
 * matches the order number or any of its event names; sorting is by the first
 * event's date or name, or by how soon the order expires.
 */
export const filterAndSortOrders = (orders: OrderResponse[], search: string, sortBy: 'date' | 'name' | 'expiry'): OrderResponse[] => {
  const searchLower = search.toLowerCase();
  const filtered = search ? orders.filter(order => (order.id?.toString().toLowerCase().includes(searchLower) || order.events?.some(event => event.name?.toLowerCase().includes(searchLower))) ?? false) : orders;
  return [...filtered].sort((a, b) => {
    if (sortBy === 'expiry') {
      // Soonest deadline first; an order that never expires has no place in
      // the countdown and sits at the end.
      return expiryRank(a) - expiryRank(b);
    }
    if (sortBy === 'date') {
      const dateA = a.events?.[0]?.startDate ? new Date(a.events[0].startDate).getTime() : 0;
      const dateB = b.events?.[0]?.startDate ? new Date(b.events[0].startDate).getTime() : 0;
      return dateA - dateB;
    }
    const nameA = a.events?.[0]?.name?.toLowerCase() ?? '';
    const nameB = b.events?.[0]?.name?.toLowerCase() ?? '';
    return nameA.localeCompare(nameB);
  });
};

/**
 * The image and title an order is represented by in a list. An order made up of
 * nothing but one subscription is shown as that subscription; otherwise the
 * first event wins, then gift cards, memberships, donations and products.
 */
export const getOrderImageAndTitle = (order: OrderResponse, intl: IntlShape): {
  imageUrl: string;
  title: string;
} => {
  const subscription = getSoleSubscriptionGroup(order);
  if (subscription) {
    return {
      imageUrl: subscription.imageUrl ?? '',
      title: subscription.name ?? ''
    };
  }
  if (order.events && order.events.length > 0) {
    const firstEvent = order.events[0];
    const name = firstEvent?.name ?? '';
    const additionalEventsCount = order.events.length - 1;
    const title = additionalEventsCount > 0 ? `${name} ${intl.formatMessage({
      id: "account.orders.multiEventTitleSuffix",
      defaultMessage: [{
        "type": 0,
        "value": "(+"
      }, {
        "type": 6,
        "value": "count",
        "options": {
          "one": {
            "value": [{
              "type": 7
            }, {
              "type": 0,
              "value": " other event"
            }]
          },
          "other": {
            "value": [{
              "type": 7
            }, {
              "type": 0,
              "value": " other events"
            }]
          }
        },
        "offset": 0,
        "pluralType": "cardinal"
      }, {
        "type": 0,
        "value": ")"
      }]
    }, {
      count: additionalEventsCount
    })}` : name;
    return {
      imageUrl: firstEvent?.imageUrl ?? '',
      title
    };
  }
  if (order.giftCards && order.giftCards.length > 0) {
    const first = order.giftCards[0];
    return {
      imageUrl: first?.imageUrl ?? '',
      title: first?.name ?? ''
    };
  }
  if (order.memberships && order.memberships.length > 0) {
    const first = order.memberships[0];
    return {
      imageUrl: first?.imageUrl ?? '',
      title: first?.name ?? ''
    };
  }
  if (order.donations && order.donations.length > 0) {
    const first = order.donations[0];
    return {
      imageUrl: first?.imageUrl ?? '',
      title: first?.name ?? ''
    };
  }
  if (order.products && order.products.length > 0) {
    const first = order.products[0];
    return {
      imageUrl: first?.imageUrl ?? '',
      title: first?.name ?? ''
    };
  }
  return {
    imageUrl: '',
    title: ''
  };
};